/* ==========================================================================
   assets/upgrade-offer-ui.js -- window.UpgradeOfferUI

   Added 29 Sep 2026 (F10, LOG-027 "Obsidian"; foundation task, contract
   8.7/8.8). The VISIBLE layer over window.UpgradeOffer (assets/upgrade-
   offer.js), which is pure data with no DOM, no timers and no clock of its
   own beyond the caller's `now`. This file supplies the one clock the
   theme trusts (D6: the device clock corrected by a server offset), the
   header bar, the countdown (inline and tiles), the footer's "Offer terms"
   reveal, and the reload rules -- and exposes a small public API so the
   home and commerce streams can build the banner, the PDP label, the card
   pill and the cart line on top of it without duplicating any of this.

   LOADED ONLY INSIDE THE SAME GATE AS upgrade-offer.js (layout/theme.liquid,
   `{%- if ug_of_on or ug_of_any_msg %}`). window.UpgradeOfferUI THEREFORE
   EXISTS ONLY WHEN THE GATE IS ON -- every consumer must guard with
   `if (window.UpgradeOfferUI)` first (contract 8.8's own header line).

   THE CLOCK (D6, spec C1 amendment). A Liquid `now` cannot be trusted to
   decide whether an offer's window is open (Shopify's own docs: a
   server-rendered `now`/`date` "might not be updated for every page view,
   depending on the context and caching") -- upgrade-offer.js's own header
   already makes that case for the WINDOW check. The visitor's raw device
   clock is not good enough either: a phone with a wrong clock could show a
   countdown to an offer that has not really started, or keep one alive
   past its real end. So `now()` here is the device clock corrected by an
   offset read ONCE, from the `Date` response header of an uncached,
   same-origin `fetch('/cart.js', { cache: 'no-store' })` -- a request the
   storefront already needs for other reasons, so this adds no new
   endpoint. If the offset cannot be read (clockTrusted() false), every
   surface falls back to showing the end date only, never a countdown built
   on a clock nobody can vouch for. (Until 31 Oct 2026 an offset above
   +/-120 seconds was refused too; a device an hour out then lost its
   countdown although the corrected clock was exact.) There is
   NO URL OVERRIDE in this file (unlike the design prototype's `?now=`) --
   a production visitor must never be able to preview an offer before it
   starts by editing the address bar; the audit reviews a test copy with
   the offer's own dates set to the review window instead.

   ZERO TRACE WHEN OFF is one gate away (contract 8.7): every mount this
   file paints already exists in the DOM only when its OWN Liquid gate is
   true, and starts `hidden` and empty. Dynamic content (card pills, "Was",
   the deal-bar pill) is created only when this object exists AND returns a
   real offer/message -- so a caller can safely call
   `window.UpgradeOfferUI && UpgradeOfferUI.forSku(...)` on every page,
   offer on or off, and get null back with the gate off.

   MERCHANT STRINGS ARE INSERTED WITH textContent ONLY, NEVER innerHTML,
   throughout this file -- the same rule upgrade-offer.js's own header
   documents for the JSON payload, carried through to the DOM side.

   Usage (by other streams' own markup and inline scripts; none of it here):
     if (window.UpgradeOfferUI) {
       UpgradeOfferUI.ready(function (ui) {
         var offer = ui.offer();
         ...
       });
     }

   Same extraction convention as assets/upgrade-offer.js and assets/upgrade-
   deal.js: a plain self-invoking function wrapper over bare `document`/
   `window`/`sessionStorage`/`location`/`fetch`/`console` globals in the
   shipped file, so the test harness can sandbox all six the same way
   tests/totals.test.js already does for upgrade-deal.js (`new
   Function('document','window','sessionStorage','location','fetch',
   'console', src)`), with no change to this file's own structure. NOTE FOR
   THE NEXT EDITOR: do not spell that wrapper's own opening and closing
   syntax literally in a comment anywhere in this file -- the test harness
   locates the real one by searching for the first, exact occurrence of it,
   and a comment repeating it verbatim would be found first and truncate
   the extracted source (caught while writing this file's own test).
   ========================================================================== */
(function () {
  'use strict';
  /* ---- ARABIC PHASE 4, 4 Oct 2026 (LOG-043; 2026-10-04_arabic/phase4-home-shop.md)
     OFFERS STAY OFF (G9): this file loads only inside the offer gate. This
     makes the bar, the countdown and the footer's "Offer terms" ready for a
     translated page. Every word this file prints is in EN below, exactly as
     it was written inline before, read through T() / TN(). An English page
     has no dictionary, so T() returns EN's text and every surface is
     identical by construction (tests/obsidian-foundation.test.js SUITE 9 and
     tests/obsidian-content.test.js SUITE 4b run this file with no
     window.UpgradeI18n). On a translated page:
       - the dates name the month in the page language (offer.month_*; the
         merchant's dates are Cairo wall-clock strings, read as before);
       - the countdown says "فاضل ..." with Arabic plurals (offer.days /
         hours / minutes);
       - the merchant's bilingual fields are read through pick() (the Arabic
         when it is set, else the English) and the separate Arabic line the
         English bar carries is left out, because the main line IS the
         Arabic. "Arabic only" on /ar is provisional: mott-voice decides
         before offers return (00-PLAN.md section 5).
     The data plumbing in offer() and messages() copies the merchant's {en,
     ar} record as it is (tools/build-i18n.js EN_READ_DATA_FUNCTIONS). */
  var EN = {
    'offer.month_jan': 'Jan',
    'offer.month_feb': 'Feb',
    'offer.month_mar': 'Mar',
    'offer.month_apr': 'Apr',
    'offer.month_may': 'May',
    'offer.month_jun': 'Jun',
    'offer.month_jul': 'Jul',
    'offer.month_aug': 'Aug',
    'offer.month_sep': 'Sep',
    'offer.month_oct': 'Oct',
    'offer.month_nov': 'Nov',
    'offer.month_dec': 'Dec',
    'offer.date_short': '{day} {month}',
    'offer.date_long': '{day} {month} {year}, {time} Cairo time',
    'offer.tile_days': 'Days',
    'offer.tile_hours': 'Hrs',
    'offer.tile_minutes': 'Min',
    'offer.tile_seconds': 'Sec',
    'offer.under_a_minute_tiles': 'Under a minute left',
    'offer.ends': 'Ends {date}',
    'offer.until': 'Until {date}',
    'offer.bar_until': '· Until {date}',
    'offer.prev_message': 'Previous message',
    'offer.next_message': 'Next message',
    'offer.terms': 'Offer terms',
    'offer.under_a_minute': 'under a minute left',
    'offer.days': { one: '{count} day', other: '{count} days' },
    'offer.hours': { one: '{count} hrs', other: '{count} hrs' },
    'offer.minutes': { one: '{count} min', other: '{count} min' },
    'offer.part_sep': ' ',
    'offer.left': '{time} left'
  };
  var I18N = (typeof window !== 'undefined' && window.UpgradeI18n) || null;
  /* ---- EDIT EVERYTHING, 7 Oct 2026 (2026-10-06_lynkrs-tech-fixes/
     edit-everything-7oct/build/tradein.md). The bar's and the countdown's
     words are settings of sections/upgrade-deal-bar.liquid ("Trade-up deal
     bar words" > "Offer bar and countdown", in the footer group), in the
     same inert tag as the deal bar's, data-ug-deal-words, keyed by the EN
     key. OWN() hands T() the section's word only when it has something in
     it, its {placeholders} and digits are EN's, and, on a translated page,
     it is not EN's own text. Otherwise T() is what it was. Month names,
     date formats, plurals and the joiner stay locale keys (formats, rule
     R4). Every sink sets these as text. */
  var OWN_WORDS = readOwnWords();
  function readOwnWords() {
    try {
      if (typeof document === 'undefined' || !document || typeof document.querySelector !== 'function') { return {}; }
      var el = document.querySelector('script[data-ug-deal-words]');
      if (!el) { return {}; }
      var o = JSON.parse(el.textContent || '{}');
      return (o && typeof o === 'object') ? o : {};
    } catch (e) { return {}; }
  }
  function placeholdersOf(s) {
    var seen = {};
    (String(s).match(/\{\w+\}/g) || []).forEach(function (p) { seen[p] = true; });
    return Object.keys(seen).sort().join(',');
  }
  function digitsOf(s) {
    return (String(s).replace(/\{\w+\}/g, '').match(/\d+/g) || []).sort().join(',');
  }
  function OWN(key) {
    var v = Object.prototype.hasOwnProperty.call(OWN_WORDS, key) ? OWN_WORDS[key] : null;
    var en = EN[key];
    if (typeof v !== 'string' || !/\S/.test(v) || typeof en !== 'string') { return null; }
    if (placeholdersOf(v) !== placeholdersOf(en) || digitsOf(v) !== digitsOf(en)) { return null; }
    if (I18N && v === en) { return null; }
    // FIX PASS, 7 Oct 2026 (fixes/FIXES.md D27): on a translated page a field is used only when it carries
    // Arabic script, or the dictionary has no such key; until its Arabic is registered the dictionary shows.
    if (I18N && !/[\u0600-\u06FF]/.test(v)) {
      try { if (I18N.has(key)) { return null; } } catch (eA) { return null; }
    }
    return v;
  }
  // {name} -> value, each value isolated on a right-to-left page (once).
  function fillOwn(str, vars) {
    return String(str).replace(/\{(\w+)\}/g, function (whole, name) {
      return (vars && Object.prototype.hasOwnProperty.call(vars, name) && vars[name] !== null && vars[name] !== undefined)
        ? isolateText(vars[name]) : whole;
    });
  }
  // {name} -> value; an unknown {name} is left as it is.
  function fill(str, vars) {
    return String(str == null ? '' : str).replace(/\{(\w+)\}/g, function (whole, name) {
      return (vars && Object.prototype.hasOwnProperty.call(vars, name) && vars[name] !== null && vars[name] !== undefined)
        ? String(vars[name]) : whole;
    });
  }
  // The section's word (edit everything), else the dictionary's text when it
  // has the key, else EN's. Never throws.
  function T(key, vars) {
    var own = OWN(key);
    if (own !== null) { return fillOwn(own, vars); }
    if (I18N) {
      try {
        if (I18N.has(key)) {
          var tr = I18N.t(key, vars);
          if (typeof tr === 'string') { return tr; }
        }
      } catch (e) { /* fall back to English */ }
    }
    return fill(EN[key], vars);
  }
  // Plurals: EN[key] is { one, other } (English); the dictionary carries
  // key_zero ... key_other and picks by the page language's plural rules.
  function TN(key, n, vars) {
    var v = {};
    Object.keys(vars || {}).forEach(function (k) { v[k] = vars[k]; });
    if (v.count === undefined) { v.count = n; }
    if (I18N) {
      try {
        if (I18N.has(key + '_other')) {
          var tr = I18N.plural(key, n, v);
          if (typeof tr === 'string') { return tr; }
        }
      } catch (e) { /* fall back to English */ }
    }
    var forms = EN[key];
    if (forms && typeof forms === 'object') { return fill(n === 1 ? forms.one : forms.other, v); }
    return fill(forms, v);
  }
  // The merchant's bilingual field {en, ar}: the page language's text when it
  // is set, else the English (UpgradeI18n.pick). English page: obj.en, as before.
  function pick(obj) {
    if (I18N && typeof I18N.pick === 'function') {
      try { var s = I18N.pick(obj); if (typeof s === 'string') { return s; } } catch (e) { /* English */ }
    }
    return obj ? obj.en : obj;
  }
  function isolateText(value) {
    var str = String(value == null ? '' : value);
    try {
      if (I18N && typeof I18N.dir === 'function' && I18N.dir() === 'rtl' && typeof I18N.isolate === 'function') { return I18N.isolate(str); }
    } catch (e) { /* English behaviour */ }
    return str;
  }
  if (window.UpgradeOfferUI) { return; }

  /* This file's own <script> tag, read once while it runs: it carries the two
     card words paintCards() prints (layout/theme.liquid, 29 Oct 2026). */
  var SELF_TAG = (typeof document !== 'undefined' && document && document.currentScript) || null;

  var TRUST_WINDOW_MS = 120000;   /* +/-120 seconds (contract 8.8); since 31 Oct 2026 informational only, see clockTrusted() */
  var READY_TIMEOUT_MS = 1500;    /* the cap ready() waits before giving up */
  var TICK_MS = 60 * 1000;        /* the minute ticker: every countdown and the bar */
  var SEC_MS = 1000;              /* the seconds ticker: the Black Friday hero's Sec tile only, while it is on screen */

  /* ---- small helpers ------------------------------------------------- */

  function hasOwn(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }

  function flags() {
    return (window.UpgradeFlags && typeof window.UpgradeFlags === 'object') ? window.UpgradeFlags : {};
  }

  function rawOffer() {
    var o = flags().offer;
    return (o && typeof o === 'object') ? o : null;
  }

  function offerApi() {
    var api = window.UpgradeOffer;
    return (api && typeof api === 'object') ? api : null;
  }

  var MONTHS_SHORT = [T('offer.month_jan'), T('offer.month_feb'), T('offer.month_mar'), T('offer.month_apr'),
    T('offer.month_may'), T('offer.month_jun'), T('offer.month_jul'), T('offer.month_aug'), T('offer.month_sep'),
    T('offer.month_oct'), T('offer.month_nov'), T('offer.month_dec')];

  /* Merchant date fields are Cairo WALL-CLOCK strings, "YYYY-MM-DD HH:MM",
     entered by a human as Cairo local time -- formatting them for display
     is therefore reading the string's own components back, not another
     timezone conversion (upgrade-offer.js's _cairoWallToUtcMs already did
     the one conversion that matters: deciding whether the window is open). */
  function parseWall(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/.exec(String(s == null ? '' : s).trim());
    if (!m) { return null; }
    return { y: +m[1], mo: +m[2], d: +m[3], h: +m[4], mi: +m[5] };
  }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function endShortOf(s) {
    var p = parseWall(s);
    return p ? T('offer.date_short', { day: p.d, month: MONTHS_SHORT[p.mo - 1] }) : '';
  }
  function endLongOf(s) {
    var p = parseWall(s);
    return p ? T('offer.date_long', { day: p.d, month: MONTHS_SHORT[p.mo - 1], year: p.y, time: pad2(p.h) + ':' + pad2(p.mi) }) : '';
  }

  /* ---- the clock: device clock + a server offset, read once ----------- */

  var offsetMs = 0;
  var offsetKnown = false;
  var readySettled = false;
  var readyCallbacks = [];

  /* 31 Oct 2026 (Omar: "they need to see the counter"): a device clock that is
     wrong by more than TRUST_WINDOW_MS no longer drops the countdown to the end
     date. Once Shopify's Date header has been read, now() below IS Shopify's
     clock, whatever the device says, so any known offset is good enough. Only
     an offset that could not be read at all still falls back to the date. */
  function clockTrusted() {
    return offsetKnown;
  }

  function now() {
    return clockTrusted() ? (Date.now() + offsetMs) : Date.now();
  }

  function settleReady() {
    if (readySettled) { return; }
    readySettled = true;
    var cbs = readyCallbacks;
    readyCallbacks = [];
    cbs.forEach(function (cb) {
      try { cb(api); } catch (e) { /* a consumer's own bug must not break the others */ }
    });
  }

  function syncClock() {
    var settledLocally = false;
    var timer = setTimeout(function () {
      if (settledLocally) { return; }
      settledLocally = true;
      settleReady();
    }, READY_TIMEOUT_MS);

    function finish() {
      if (settledLocally) { return; }
      settledLocally = true;
      clearTimeout(timer);
      settleReady();
    }

    try {
      var req = fetch('/cart.js', { cache: 'no-store' });
      if (!req || typeof req.then !== 'function') { finish(); return; }
      req.then(function (res) {
        var late = settledLocally;
        try {
          var dateHeader = (res && res.headers && typeof res.headers.get === 'function') ? res.headers.get('Date') : null;
          if (dateHeader) {
            var serverMs = Date.parse(dateHeader);
            if (isFinite(serverMs)) {
              offsetMs = serverMs - Date.now();
              offsetKnown = true;
            }
          }
        } catch (e) { /* leave the clock untrusted */ }
        finish();
        // 31 Oct 2026 (Omar: "they need to see the counter"): the Date header can land after the 1.5 s cap on
        // slow data, when every mount has already painted the end date. Repaint them all now, silently.
        if (late && offsetKnown) { clockArrived(); }
      }, finish);
    } catch (e) {
      finish();
    }
  }

  function ready(cb) {
    if (typeof cb !== 'function') { return; }
    if (readySettled) { cb(api); return; }
    readyCallbacks.push(cb);
  }

  /* ---- F5, 29 Sep 2026 (fix_now, prices-on-page P6) ---------------------
     active() alone only proves the switch is on, every display field is
     valid, and `now` falls in the window -- it says nothing about whether
     any actual SKU is reduced. The TEST copy's own state (offer on, 0
     compare-at prices anywhere) proved that gap: a live offer bar, banner,
     countdown and footer "Offer terms" link could all advertise a price cut
     that applies to zero products (a Law 181/2018 Art 9(10) risk). The
     money path was already safe -- assets/upgrade-deal.js's offerRateFor()
     has required a real skuOnOffer since LOG-029 -- this closes the same
     gap on the WORDS.

     anyOnOfferOrClosed(): true only when window.UpgradePrices.anyOnOffer()
     (the collapsed, conflict-safe map over every device/grade this page's
     collection seed reached) says at least one SKU is genuinely reduced,
     AND -- wherever window.UpgradeFacts.inStock exists -- at least one of
     those on-offer SKUs is also in stock. A missing accessor on either
     object (an older cached upgrade-price-map.liquid render, a theme still
     mid-deploy) answers false: FAIL CLOSED, never "assume yes because the
     function is not there to say no". */
  function anyOnOfferOrClosed() {
    var pricesApi = (window.UpgradePrices && typeof window.UpgradePrices === 'object') ? window.UpgradePrices : null;
    if (!pricesApi || typeof pricesApi.anyOnOffer !== 'function') { return false; }
    var anyOn = false;
    try { anyOn = pricesApi.anyOnOffer() === true; } catch (e) { anyOn = false; }
    if (!anyOn) { return false; }

    var facts = (window.UpgradeFacts && typeof window.UpgradeFacts === 'object') ? window.UpgradeFacts : null;
    if (!facts || typeof facts.inStock !== 'function') { return true; } /* no stock fact available: stock cannot gate what it cannot see */

    if (typeof pricesApi.isOnOffer !== 'function' || typeof pricesApi.all !== 'function') { return false; } /* fail closed: cannot enumerate to check stock */
    var all = null;
    try { all = pricesApi.all(); } catch (e) { all = null; }
    if (!all || typeof all !== 'object') { return false; }

    var skus = Object.keys(all);
    for (var i = 0; i < skus.length; i++) {
      var sku = skus[i];
      var on = false;
      try { on = pricesApi.isOnOffer(sku) === true; } catch (e) { on = false; }
      if (!on) { continue; }
      var stocked = null;
      try { stocked = facts.inStock(sku); } catch (e) { stocked = null; }
      if (stocked === true) { return true; }
    }
    return false;
  }

  /* ---- the offer, in display form -------------------------------------
     Wraps window.UpgradeOffer.active()/the raw record with THIS file's
     corrected clock, so every consumer (the bar, the banner, the PDP
     label, the cart line) reads the same "is it live right now" answer. */

  /* ---- PREVIEW BEFORE LAUNCH (30 Oct 2026, 2.1 - dev Black Friday) -------
     Omar: "i wanna see it on shopify before i publish it so my content team
     can go through everything". With Theme settings > Upgrade · Black Friday
     > "Preview Black Friday before launch" ticked, layout/theme.liquid adds
     bfPreview: true to window.UpgradeFlags -- but only on an UNPUBLISHED
     theme (theme.role) -- and this file honours it only while Shopify itself
     does not report the theme as live (Shopify.theme.role !== 'main'). Two
     locks, so a live theme can never preview.
     In preview the offer reads as running and every in-stock phone as on
     offer, so the banners, countdown, card badges, deal-bar pill, product-
     page label and cart line all show -- with no struck price and no
     percentage, exactly as November runs. DISPLAY ONLY: the money path
     (window.UpgradeOffer, the price map, upgrade-deal.js's bonus) is never
     told, so no price, bonus or order note changes. */
  function previewing() {
    if (flags().bfPreview !== true) { return false; }
    try {
      // Allow-list, as the Liquid gate: an unpublished or development theme only.
      var th = window.Shopify && window.Shopify.theme;
      if (!th || (th.role !== 'unpublished' && th.role !== 'development')) { return false; }
      // AND Shopify's own preview bar must be on the page. Only a merchant preview (the
      // Preview button, a shared preview link) gets it; a theme served to real visitors by a
      // Shopify Rollouts launch, event or experiment does not, so it never previews (review
      // finding, 30 Oct 2026: the theme role alone is not proof that no customer sees it).
      if (!document.getElementById('OnlineStorePreviewBarNextData')) { return false; }
    } catch (e) { return false; }
    var raw = rawOffer();
    return raw !== null && typeof raw.ends === 'string';
  }
  // The synthetic per-phone results only while NO real offer runs: once the sale is live,
  // a phone that missed its price cut must show as not reduced, preview or not.
  function previewOnly() { return previewing() && !liveOffer(); }
  function previewOffer() {
    var raw = rawOffer();
    var api2 = offerApi();
    var nm = (raw.name && typeof raw.name === 'object') ? raw.name : {};
    var wh = (raw.what && typeof raw.what === 'object') ? raw.what : {};
    return {
      name: { en: nm.en || '', ar: nm.ar || '' },
      what: { en: wh.en || '', ar: wh.ar || '' },
      endsAt: (api2 && typeof api2._cairoWallToUtcMs === 'function') ? api2._cairoWallToUtcMs(raw.ends) : null,
      endShort: endShortOf(raw.ends),
      endLong: endLongOf(raw.ends),
      termsUrl: raw.termsUrl || '',
      showCountdown: raw.showCountdown === true,
      showWas: false
    };
  }

  function offer() {
    var live = liveOffer();
    if (live) { return live; }
    return previewing() ? previewOffer() : null;
  }

  function liveOffer() {
    var api2 = offerApi();
    if (!api2 || typeof api2.active !== 'function') { return null; }
    var n = now();
    if (!api2.active(n)) { return null; }
    if (!anyOnOfferOrClosed()) { return null; } /* F5: no SKU actually on offer (and in stock, where knowable) -- show nothing */
    var raw = rawOffer();
    if (!raw) { return null; }
    var endsAt = (typeof api2._cairoWallToUtcMs === 'function') ? api2._cairoWallToUtcMs(raw.ends) : null;
    return {
      name: { en: raw.name.en, ar: raw.name.ar },
      what: { en: raw.what.en, ar: raw.what.ar },
      endsAt: endsAt,
      endShort: endShortOf(raw.ends),
      endLong: endLongOf(raw.ends),
      termsUrl: raw.termsUrl,
      showCountdown: raw.showCountdown === true,
      showWas: raw.showWas === true
    };
  }

  /* Up to 3 "don't miss it" lines, price offer first (contract 8.8). Free
     delivery is never a message (LOG-028: it is standing policy, not an
     offer or a partnership) -- it never reaches window.UpgradeFlags.messages
     in the first place, so there is nothing to filter out here. */
  function messages() {
    var out = [];
    var o = offer();
    if (o) {
      out.push({
        kind: 'price',
        text: {
          en: o.name.en + '—' + o.what.en,
          ar: (o.name.ar && o.what.ar) ? o.name.ar + '—' + o.what.ar : ''
        },
        link: o.termsUrl,
        endsAt: o.endsAt,
        endShort: o.endShort
      });
    }
    var api2 = offerApi();
    if (api2 && typeof api2.messages === 'function') {
      var n = now();
      var raw = api2.messages(n) || [];
      raw.forEach(function (m) {
        if (out.length >= 3) { return; }
        out.push({
          kind: m.kind,
          text: { en: m.text.en, ar: m.text.ar },
          link: m.link,
          endsAt: (typeof api2._cairoWallToUtcMs === 'function') ? api2._cairoWallToUtcMs(m.ends) : null,
          endShort: endShortOf(m.ends)
        });
      });
    }
    return out.slice(0, 3);
  }

  /* forSku(): the exact same UpgradeOffer.forSku(), with THIS file's
     corrected `now` injected so every caller across every surface agrees
     on the clock without each having to sync one itself. */
  function forSku(skuOrCode, grade, opts) {
    var api2 = offerApi();
    if (!api2 || typeof api2.forSku !== 'function') { return null; }
    var o = (opts && typeof opts === 'object') ? opts : {};
    var merged = {};
    for (var k in o) { if (hasOwn(o, k)) { merged[k] = o[k]; } }
    merged.now = now();
    var res = api2.forSku(skuOrCode, grade, merged);
    if (res || o.available !== true || skuOrCode == null || skuOrCode === '' || !previewOnly()) { return res; }
    // Preview before launch: an in-stock phone reads as on offer at its own
    // real price, with no "was" and no percentage. See previewing() above.
    var pr = window.UpgradePrices, sku = skuOrCode, price = null;
    try {
      if (grade != null && grade !== '' && pr && typeof pr.skuFor === 'function') { sku = pr.skuFor(skuOrCode, grade); }
      price = (pr && typeof pr.get === 'function') ? pr.get(sku) : null;
    } catch (e) { price = null; }
    if (typeof price !== 'number' || !isFinite(price) || !(price > 0)) { return null; }
    var po = previewOffer();
    return { price: price, was: null, saving: null, pct: null, onOffer: true, name: po.name, ends: rawOffer().ends, termsUrl: po.termsUrl };
  }

  /* cardOffer({skus, fromSku}) -- the README's card "Was" rule (D9): a
     strike only when the offer's own on-offer SKU is the card's own
     pre-offer "From" SKU (its grade, any colour: 1 Nov 2026, below);
     otherwise "on some grades" and no strike.
     REQUIRES window.UpgradeFacts.inStock() -- with no stock fact for a SKU
     it is treated exactly like "not in stock" (fail closed: no pill, no
     "Was", per spec C3's sold-out guardrail and decision D9). Picks the
     CHEAPEST on-offer, in-stock SKU among the ones given, matching the
     "From" price rule every card already uses. */
  function cardOffer(opts) {
    var o = (opts && typeof opts === 'object') ? opts : {};
    var skus = Array.isArray(o.skus) ? o.skus : [];
    var fromSku = (o.fromSku != null) ? String(o.fromSku) : null;
    var api2 = offerApi();
    var facts = window.UpgradeFacts;
    if (!api2 || typeof api2.forSku !== 'function') { return null; }
    if (!facts || typeof facts.inStock !== 'function') { return null; }
    if (!skus.length) { return null; }
    var n = now();
    var best = null;
    skus.forEach(function (sku) {
      if (facts.inStock(sku) !== true) { return; }
      var r = api2.forSku(sku, null, { now: n, available: true });
      if (!r) { return; }
      if (!best || r.price < best.result.price) { best = { sku: String(sku), result: r }; }
    });
    if (!best && previewOnly()) {
      // Preview before launch: any in-stock SKU gets the badge (name only).
      var pName = previewOffer().name;
      if (!pName.en) { return null; }
      var anyIn = skus.some(function (sku) { return facts.inStock(sku) === true; });
      return anyIn ? { label: pName, onSome: false, was: null, pct: null } : null;
    }
    if (!best) { return null; }
    /* 1 Nov 2026 (push-01nov-instockfrom): a caller that prints best's price as the card's "From" asks
       with rebase: true (paintCards does). The answer then carries that price, and the "Was" and the
       -N% are best's own. Without it, the answer below is exactly what it was. */
    if (o.rebase === true) {
      var rebased = rebaseResult(best, skus, facts, api2, n);
      if (rebased) { return rebased; }
    }
    /* 1 Nov 2026 (push-01nov-cardoffer): the "From" SKU is matched by GRADE, never by colour.
       The Liquid card names a colour variant as its From SKU (<code>-<RX>-<CLR>, a variant at
       price_min, an available one when there is one), and best.sku is the first colour of the same
       grade in the card's list, so the old string test (fromSku !== best.sku) dropped the "Was" and
       the -N% on many Liquid cards although both are one grade, one price, one compare-at. The From SKU is
       now asked through the SAME two calls that picked best: UpgradeFacts.inStock (per grade) and
       UpgradeOffer.forSku, whose price map reads (get, isOnOffer, getWas) collapse a colour SKU
       onto its grade key -- no SKU string is cut here. The strike stands only when the From SKU
       is in stock and genuinely reduced, its own offer price is the card's cheapest (best's), and
       the "Was" and -N% are its OWN. A From SKU that is best.sku itself (the shop grid and the
       home rail script pass grade SKUs) answers exactly as before; no From SKU: unchanged. */
    var fromRes = null;
    if (fromSku != null) {
      fromRes = (fromSku === best.sku) ? best.result
        : ((facts.inStock(fromSku) === true) ? api2.forSku(fromSku, null, { now: n, available: true }) : null);
    }
    var onSome = (fromSku != null) && !(fromRes && fromRes.price === best.result.price);
    var own = onSome ? null : (fromRes || best.result);
    var was = (own && own.was != null) ? own.was : null;
    // 29 Oct 2026 (LOG-066): the percentage rides with the "Was", under the
    // same rule -- only beside a lawful struck price, never on its own.
    var pct = (was != null && typeof own.pct === 'number') ? own.pct : null;
    return { label: best.result.name, onSome: onSome, was: was, pct: pct };
  }

  /* rebaseResult() -- 1 Nov 2026 (push-01nov-instockfrom). cardOffer()'s answer for a card that prints the
     cheapest IN-STOCK, ON-OFFER price as its "From". The old "From" is the cheapest price whether or not a unit
     is in stock, so a sold-out 128 GB at 38,899 hid the in-stock 256 GB at 43,599 and its -10%. Here the card's
     price becomes best's, and the "Was" and -N% are best's own compare-at, so a "Was" still stands only beside
     the exact price it belongs to. It answers null (the caller keeps cardOffer()'s old answer: same price, no
     strike) when the price map is missing or has no price for an in-stock SKU, or when a cheaper unit can be
     bought that is NOT on offer (best would not be the card's true "From"). It keeps the new price but drops the
     "Was" and -N% when another in-stock SKU at best's price is reduced differently, or not at all: one price
     with two "Was" figures, or a "Was" over an unreduced unit, is no lawful strike. It reads only what
     cardOffer() reads (UpgradeFacts.inStock per grade, UpgradeOffer.forSku) and UpgradePrices.get. */
  function rebaseResult(best, skus, facts, api2, n) {
    var pr = window.UpgradePrices;
    if (!pr || typeof pr.get !== 'function') { return null; }
    var price = best.result.price;
    var same = true;
    for (var i = 0; i < skus.length; i++) {
      if (facts.inStock(skus[i]) !== true) { continue; }
      var p = pr.get(skus[i]);
      if (typeof p !== 'number' || !isFinite(p) || p < price) { return null; }
      if (p !== price) { continue; }
      var r = (String(skus[i]) === best.sku) ? best.result : api2.forSku(skus[i], null, { now: n, available: true });
      if (!r || r.price !== price || r.was !== best.result.was || r.pct !== best.result.pct) { same = false; }
    }
    var was = (same && best.result.was != null) ? best.result.was : null;
    var pct = (was != null && typeof best.result.pct === 'number') ? best.result.pct : null;
    return { label: best.result.name, onSome: false, was: was, pct: pct, price: price, sku: best.sku };
  }

  /* The card's price figure alone, as the two card builders print it (the page's number format, isolated on
     a right-to-left page; English: the en-US figure), and the place a SKU names (grade key and storage) in
     the words the product page reads from a link (?grade=g&storage=256GB). SUFFIX is the price map's own
     { g: 'RG', p: 'RP', d: 'RD' }. Anything it cannot read answers null. */
  function cardFigure(n) {
    if (I18N && typeof I18N.num === 'function') {
      try { var d = I18N.num(Math.round(n)); if (typeof d === 'string') { return isolateText(d); } } catch (e) { /* English */ }
    }
    return Math.round(n).toLocaleString('en-US');
  }
  function skuPlace(sku) {
    var pr = window.UpgradePrices;
    var suffix = (pr && pr.SUFFIX && typeof pr.SUFFIX === 'object') ? pr.SUFFIX : null;
    var segs = String(sku == null ? '' : sku).trim().toUpperCase().split('-');
    var grade = null;
    if (suffix) { ['g', 'p', 'd'].forEach(function (k) { if (suffix[k] && segs.indexOf(suffix[k]) > 0) { grade = k; } }); }
    var m = /^(\d+)(T?)$/.exec(segs[1] || '');
    return (grade && m) ? { grade: grade, storage: m[1] + (m[2] ? 'TB' : 'GB') } : null;
  }
  function cardHref(href, where) {
    var i = href.indexOf('?');
    var keep = (i < 0 ? '' : href.slice(i + 1)).split('&').filter(function (part) { return part && !/^(variant|grade|storage)=/.test(part); });
    keep.push('grade=' + where.grade, 'storage=' + where.storage);
    return (i < 0 ? href : href.slice(0, i)) + '?' + keep.join('&');
  }

  /* rebaseCard() -- prints rebaseResult()'s price as the card's "From" (the text node first inside .ug-price,
     which is what both card builders print; the currency stays) and, when that price is not the one the
     card's link was built for, sends the link to the unit that has it, so the page it opens shows the price
     the card showed (a link to the sold-out unit opens "Out of stock in this grade", with no price and no -N%).
     False, with nothing changed, when the figure is not where the builders put it. */
  function rebaseCard(card, anchor, res, fromSku) {
    var fig = (anchor.classList && anchor.classList.contains('ug-price')) ? anchor : anchor.querySelector('.ug-price');
    var text = fig ? fig.firstChild : null;
    if (!text || text.nodeType !== 3) { return false; }
    var pr = window.UpgradePrices, fromPrice = null;
    try { fromPrice = (fromSku && pr && typeof pr.get === 'function') ? pr.get(fromSku) : null; } catch (e) { fromPrice = null; }
    text.nodeValue = cardFigure(res.price);
    var href = card.getAttribute('href'), where = skuPlace(res.sku);
    if (fromPrice !== res.price && where && typeof href === 'string' && href) { card.setAttribute('href', cardHref(href, where)); }
    card.setAttribute('data-ug-oc-from', res.sku);
    return true;
  }

  /* paintCards(root) -- 29 Oct 2026 (2.1 - dev Black Friday, LOG-066: 10% off
     every phone, 1-30 Nov). The card offer for every card that has no offer
     code of its own: the shop grid (assets/upgrade-catalogue.js) and the
     Liquid card (snippets/upgrade-productcard.liquid: brand pages, landing
     phones). Dawn's card (search, the default collection) is left alone: its
     contract keeps it free of the offer switches (tests/native-routes). Each
     carries data-ug-oc-skus (its SKUs, comma-separated)
     and data-ug-oc-from (the SKU behind its "From" price); its price is
     the element marked data-ug-oc-price, else its .ug-card__from. Those
     attributes are printed only while the offer switch is on.
     The rule is cardOffer()'s, unchanged: the badge when an in-stock SKU is
     genuinely reduced inside the offer window; the struck "Was" and the
     -N% only when show_was is on AND that SKU is the card's own "From"
     SKU (its grade, any colour, since 1 Nov 2026). Never "on some grades" (Omar, 29 Oct 2026). The percentage is UpgradeOffer's, from the variant's own Shopify
     compare-at price, rounded DOWN. No new words: the pill is the offer's
     own name, "Was" is the home rail's locale key (English and Arabic
     already in locales/), -N% is a figure. Text only
     through textContent. Each card is painted once (data-ug-oc-done).
     Since 1 Nov 2026 (push-01nov-instockfrom) the card's "From" is the cheapest in-stock, on-offer price, so
     the "Was" and -N% are best's own: see rebaseResult() and rebaseCard() above. */
  // The word comes from the theme's own locale files, printed by
  // layout/theme.liquid on this file's script tag as data-card-was, in the
  // page's language: the key the home rail uses (upgrade.js.home.offer_was_html).
  // Missing: nothing is shown rather than a guess.
  function cardWord(name) {
    var w = null;
    try { w = SELF_TAG ? SELF_TAG.getAttribute('data-card-' + name) : null; } catch (e) { w = null; }
    return (typeof w === 'string') ? w : '';
  }
  function cardMoney(n) {
    if (I18N && typeof I18N.money === 'function') {
      try { var m = I18N.money(n, false); if (typeof m === 'string') { return m; } } catch (e) { /* English */ }
    }
    var figure = String(n);
    try { figure = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n); } catch (e2) { /* plain digits */ }
    return figure + ' EGP';
  }
  function cardPct(n) {
    if (I18N && typeof I18N.pct === 'function') {
      try { var p = I18N.pct(n); if (typeof p === 'string') { return p; } } catch (e) { /* English */ }
    }
    return n + '%';
  }
  function paintCards(root) {
    ready(function () {
      var scope = (root && typeof root.querySelectorAll === 'function') ? root : document;
      var cards;
      try { cards = scope.querySelectorAll('[data-ug-oc-skus]:not([data-ug-oc-done])'); } catch (e) { return; }
      Array.prototype.forEach.call(cards, function (card) {
        card.setAttribute('data-ug-oc-done', '');
        var skus = String(card.getAttribute('data-ug-oc-skus') || '').split(',')
          .map(function (x) { return x.trim(); }).filter(Boolean);
        var fromSku = card.getAttribute('data-ug-oc-from') || null;
        /* 1 Nov 2026 (push-01nov-instockfrom): the card's "From" becomes the cheapest in-stock, on-offer price
           (rebase), unless the card carries a line worked out from its old "From" (a trade-in balance, a
           brand-new comparison), which is then left exactly as it was. */
        var res = cardOffer({ skus: skus, fromSku: fromSku, rebase: !card.querySelector('.ug-card__trade, .ug-vsnew') });
        if (!res) { return; }
        var anchor = card.querySelector('[data-ug-oc-price]') || card.querySelector('.ug-card__from');
        if (!anchor || !anchor.parentNode) { return; }
        if (typeof res.price === 'number' && !rebaseCard(card, anchor, res, fromSku)) {
          res = cardOffer({ skus: skus, fromSku: fromSku });   // the figure was not where expected: the card as it was
          if (!res) { return; }
        }

        // The badge: the offer's own name, and the -N% beside it when there is a
        // lawful struck price. Never "on some grades" (Omar, 29 Oct 2026:
        // everything is on sale, one clean message). upgrade-core.css draws
        // .ug-card__labels on the photo's top corner.
        var labels = document.createElement('p');
        labels.className = 'ug-card__labels';
        var pill = document.createElement('span');
        pill.className = 'ug-opill';
        pill.textContent = pick(res.label);
        labels.appendChild(pill);
        var tpl = (res.was != null) ? cardWord('was') : '';
        if (tpl && typeof res.pct === 'number' && res.pct > 0) {
          var chip = document.createElement('span');
          chip.className = 'ug-card__pct';
          chip.textContent = isolateText('\u2212' + cardPct(res.pct));
          labels.appendChild(document.createTextNode(' '));
          labels.appendChild(chip);
        }
        anchor.parentNode.insertBefore(labels, anchor);

        if (!tpl) { return; }
        var was = document.createElement('p');
        was.className = 'ug-card__was';
        was.appendChild(document.createTextNode(tpl.split('<s>')[0].split('{amount}')[0]));
        var struck = document.createElement('s');
        struck.textContent = cardMoney(res.was);
        was.appendChild(struck);
        anchor.parentNode.insertBefore(was, anchor.nextSibling);
      });
    });
  }

  /* ---- format(): the exact five phrases the countdown ever shows -------
     Public (contract 8.8 lists it directly, not underscore-prefixed), so a
     stream that builds its own countdown surface can reuse the same
     wording without re-deriving it. */
  function format(c) {
    if (!c || typeof c !== 'object') { return ''; }
    var days = c.days || 0, hours = c.hours || 0, minutes = c.minutes || 0;
    if (days === 0 && hours === 0 && minutes === 0) { return T('offer.under_a_minute'); }
    var parts = [];
    if (days > 0) { parts.push(TN('offer.days', days)); }
    if (days > 0 || hours > 0) { parts.push(TN('offer.hours', hours)); }
    parts.push(TN('offer.minutes', minutes));
    return T('offer.left', { time: parts.join(T('offer.part_sep')) });
  }

  /* ---- the tickers ------------------------------------------------------
     The MINUTE ticker moves every mounted countdown and the bar together: one
     timer, re-aimed at the next minute of the corrected clock (now()) every
     time, so it never drifts and never piles up.
     The SECONDS ticker belongs to a countdown that asked for a Sec tile (only
     the Black Friday hero does). It runs while that countdown is on screen in a
     visible tab and nowhere else. A hidden tab clears both timers; coming back
     repaints the true time at once with no motion (resumeTickers). Reduced
     motion never gets a Sec tile, so it never gets this ticker. */
  var tickers = [];   // {fn, root}: pruned as soon as the root has left the document
  var secs = [];      // {fn, root, vis, on}: the countdowns that show seconds (vis: on screen; on: counting right now)
  var tickTimer = 0;
  var secTimer = 0;
  var stageIO = null;
  var quiet = false;  // true while catching up: digits are written, nothing plays
  var RM = null;
  try { RM = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null; } catch (e) { RM = null; }

  function awake() {
    try { return document.hidden !== true && document.visibilityState !== 'hidden'; } catch (e) { return true; }
  }
  // No movement and no seconds when the visitor asked for reduced motion, and in the theme editor (the same
  // rule as upgrade-motion.js still()), so editor re-renders never replay a roll.
  function reduced() {
    try { if (document.documentElement.classList.contains('shopify-design-mode')) { return true; } } catch (e) { /* no classList */ }
    return !!(RM && RM.matches);
  }

  function pruneSecs() {
    secs = secs.filter(function (s) {
      if (s.root && s.root.isConnected) { return true; }
      try { if (stageIO && s.root) { stageIO.unobserve(s.root); } } catch (e) { /* nothing to release */ }
      return false;
    });
  }
  function tick() {
    tickers = tickers.filter(function (t) { return t.root && t.root.isConnected; });
    pruneSecs();
    tickers.slice().forEach(function (t) {
      try { t.fn(); } catch (e) { /* one broken consumer must not stop the clock for the others */ }
    });
    armSec();   // a repaint may have started (or ended) a countdown that counts seconds
  }
  // A seconds countdown needs the 1 Hz timer only while it is on screen AND counting (not in a static state).
  function live(s) { return s.vis && s.on; }
  function armTick() {
    if (tickTimer || !tickers.length || !awake()) { return; }
    tickTimer = setTimeout(function () {
      tickTimer = 0;
      tick();
      armTick();
    }, TICK_MS - (now() % TICK_MS) + 20);
  }
  function runSec(s) {
    try { s.fn(); } catch (e) { /* the minute ticker repaints it */ }
  }
  function armSec() {
    if (secTimer || reduced() || !awake()) { return; }
    pruneSecs();
    if (!secs.some(live)) { return; }
    secTimer = setTimeout(function () {
      secTimer = 0;
      secs.forEach(function (s) { if (live(s)) { runSec(s); } });
      armSec();
    }, SEC_MS - (now() % SEC_MS) + 20);
  }
  function ensureTicking() { armTick(); armSec(); }

  function pauseTickers() {
    clearTimeout(tickTimer);
    clearTimeout(secTimer);
    tickTimer = 0;
    secTimer = 0;
  }
  // The tab is visible again (or the page came back from the back/forward cache):
  // repaint the true time silently, then start the timers afresh.
  function resumeTickers() {
    pauseTickers();
    if (!awake()) { return; }
    quiet = true;
    tick();
    secs.forEach(function (s) { if (live(s)) { runSec(s); } });
    quiet = false;
    ensureTicking();
  }
  // The server clock arrived late (see syncClock): the same silent repaint, and the timers re-aimed at it.
  function clockArrived() { resumeTickers(); }
  // A countdown with seconds only ticks while it is on screen. No observer: it counts as on screen.
  function watchStage(entry) {
    if (!window.IntersectionObserver) { return; }
    entry.vis = false;   // until the observer reports (it does, once, straight away)
    try {
      stageIO = stageIO || new window.IntersectionObserver(function (list) {
        list.forEach(function (e) {
          secs.forEach(function (s) {
            if (s.root !== e.target || s.vis === e.isIntersecting) { return; }
            s.vis = e.isIntersecting;
            if (s.vis) { quiet = true; runSec(s); quiet = false; }
          });
        });
        armSec();
      });
      stageIO.observe(entry.root);
    } catch (e) { entry.vis = true; }
  }

  function isCartTemplate() {
    try {
      return !!(document.querySelector && document.querySelector('main[data-template="cart"]'));
    } catch (e) { return false; }
  }

  /* ---- the countdown, built once ---------------------------------------
     "Built once, ticks change text only" (contract 8.8): the nodes below are
     created a single time per element. A tick writes digits into them and, for
     a digit that changed, flips one attribute (data-on); the CSS does the rest.
     There is no Hide/Show timer button (31 Oct 2026, Omar): the countdown
     always shows. So that it stays calm for assistive technology, the figures
     are aria-hidden and the countdown's accessible text is its end date (the
     same words the static state prints); nothing here is a live region.

     A tile's figure is a row of ROLLING DIGITS. Each digit is two stacked
     layers; data-on says which one is entering. A change writes the new
     character into the layer that is not showing and flips data-on, so only
     the digit that changed moves (upgrade-offer-ui.css, section 02). With no
     animation at all (reduced motion) the same attribute still decides which
     layer is visible, so the figure is always the current one. */
  // A tick never replaces a text node with an identical one.
  function put(node, str) { if (node.textContent !== str) { node.textContent = str; } }

  function digitEl() {
    var el = document.createElement('span');
    el.className = 'ug-cd__digit';
    var a = document.createElement('span');
    var b = document.createElement('span');
    el.appendChild(a);
    el.appendChild(b);
    return { el: el, s: [a, b], i: 0, v: '' };
  }
  function setDigit(g, ch, roll) {
    if (g.v === ch) { return; }
    var rolling = roll && g.v !== '';
    g.v = ch;
    if (!rolling) { g.s[g.i].textContent = ch; return; }
    g.i = 1 - g.i;                 // the other layer enters, the one that showed leaves
    g.s[g.i].textContent = ch;
    g.el.setAttribute('data-on', String(g.i));
  }
  // Two digits at least ("07"). A countdown only falls, so the digits are made on the first value and never again.
  function setNum(t, n, roll) {
    var str = String(n);
    while (str.length < 2) { str = '0' + str; }
    if (str.length > t.d.length) {
      t.valueEl.textContent = '';
      t.d = [];
      for (var k = 0; k < str.length; k++) {
        var g = digitEl();
        t.valueEl.appendChild(g.el);
        t.d.push(g);
      }
    }
    while (str.length < t.d.length) { str = '0' + str; }
    for (var i = 0; i < str.length; i++) { setDigit(t.d[i], str.charAt(i), roll); }
  }

  function tileEl(label, extra) {
    var el = document.createElement('span');
    el.className = 'ug-cd__tile' + (extra || '');
    var v = document.createElement('span');
    v.className = 'ug-cd__tile-value';
    var l = document.createElement('span');
    l.className = 'ug-cd__tile-label';
    l.textContent = label;
    el.appendChild(v);
    el.appendChild(l);
    return { el: el, valueEl: v, d: [] };
  }

  function buildTiles(secWord) {
    var wrap = document.createElement('span');
    wrap.className = 'ug-cd__tiles';
    wrap.setAttribute('aria-hidden', 'true');
    var d = tileEl(T('offer.tile_days')), h = tileEl(T('offer.tile_hours')), m = tileEl(T('offer.tile_minutes'));
    var sc = secWord ? tileEl(secWord, ' ug-cd__tile--sec') : null;
    wrap.appendChild(d.el);
    wrap.appendChild(h.el);
    wrap.appendChild(m.el);
    if (sc) { sc.el.hidden = true; wrap.appendChild(sc.el); }
    var under = document.createElement('span');
    under.className = 'ug-cd__under';
    under.hidden = true;
    function hideTiles() {
      d.el.hidden = true; h.el.hidden = true; m.el.hidden = true;
      if (sc) { sc.el.hidden = true; }
    }
    return {
      wrap: wrap,
      under: under,
      // s = seconds (0-59), or -1 for none. With a Sec tile the last minute keeps counting in tiles.
      showTiles: function (c, s, roll) {
        under.hidden = true;
        var days = c.days || 0, hours = c.hours || 0, minutes = c.minutes || 0;
        var secs2 = !!sc && s >= 0;
        if (days === 0 && hours === 0 && minutes === 0 && !secs2) {
          hideTiles();
          under.hidden = false;
          put(under, T('offer.under_a_minute_tiles'));
          return;
        }
        d.el.hidden = false; h.el.hidden = false; m.el.hidden = false;
        setNum(d, days, roll);
        setNum(h, hours, roll);
        setNum(m, minutes, roll);
        if (sc) {
          sc.el.hidden = !secs2;
          if (secs2) { setNum(sc, s, roll); }
        }
      },
      setSec: function (s, roll) { setNum(sc, s, roll); },
      showText: function (s) {
        hideTiles();
        under.hidden = false;
        put(under, s);
      }
    };
  }

  /* opts.variant 'inline' (a phrase: the bar, the product page, the strip) or
     'tiles'. opts.seconds: true adds a Sec tile to tiles (the hero only). */
  function mountCountdown(el, opts) {
    if (!el || el.__ugCd) { return; }
    var variant = (opts && opts.variant === 'tiles') ? 'tiles' : 'inline';
    var cartMode = isCartTemplate();   /* LOG-026 §0.1: never a countdown in the cart */
    var withSec = variant === 'tiles' && !!opts && opts.seconds === true && !cartMode;
    // On a translated page the Sec tile waits for its Arabic (Arabic is Omar's): no English word on /ar.
    var secWord = withSec ? T('offer.tile_seconds') : '';
    if (withSec && I18N && !/[\u0600-\u06FF]/.test(secWord)) { withSec = false; }

    var root = document.createElement('span');
    root.className = 'ug-cd ug-cd--' + variant;

    var textEl = null, tiles = null, sr = null;
    if (variant === 'tiles') {
      tiles = buildTiles(withSec ? secWord : '');
      sr = document.createElement('span');
      sr.className = 'ug-visually-hidden';
      root.appendChild(tiles.wrap);
      root.appendChild(tiles.under);
      root.appendChild(sr);
    } else {
      textEl = document.createElement('span');
      textEl.className = 'ug-cd__text';
      root.appendChild(textEl);
    }

    el.appendChild(root);
    el.__ugCd = { root: root };

    var endsAt = 0, lastMin = -1, counting = false, entry = null;

    function setDisplay(s) {
      if (sr) { put(sr, ''); }
      if (textEl) { put(textEl, s); } else if (tiles) { tiles.showText(s); }
    }

    function render() {
      counting = false;
      if (entry) { entry.on = false; }
      var o = offer();
      if (!o) { root.hidden = true; return; }
      root.hidden = false;

      var staticOnly = cartMode || !o.showCountdown || !clockTrusted();
      if (staticOnly) {
        setDisplay(variant === 'tiles' ? T('offer.ends', { date: o.endLong }) : T('offer.until', { date: o.endShort }));
        return;
      }

      var n = now();
      var api2 = offerApi();
      var c = (api2 && typeof api2.countdown === 'function') ? api2.countdown(n) : null;
      var left = (typeof o.endsAt === 'number') ? o.endsAt - n : -1;
      // Preview before launch: the data layer has no countdown before the start, so count to the real
      // end the same way (upgrade-offer.js countdown()), from this file's trusted clock.
      if (!c && previewOnly() && left > 0) {
        var mTot = Math.floor(left / (60 * 1000)), dd = Math.floor(mTot / 1440), hh = Math.floor((mTot - dd * 1440) / 60);
        c = { days: dd, hours: hh, minutes: mTot - dd * 1440 - hh * 60 };
      }
      if (!c) { setDisplay(T('offer.until', { date: o.endShort })); return; }

      // The inline phrase (the bar, the product page, the strip) just changes, once a minute: motion beside a
      // price reads as a price change. Only the hero's tiles move.
      if (textEl) { put(textEl, format(c)); return; }
      var roll = !quiet && !reduced();
      var sec = -1;
      if (withSec && !reduced() && left > 0) {
        sec = Math.floor((left % TICK_MS) / SEC_MS);
        endsAt = o.endsAt;
        lastMin = Math.floor(left / TICK_MS);
        counting = true;
        if (entry) { entry.on = true; }
      }
      tiles.showTiles(c, sec, roll);
      put(sr, T('offer.ends', { date: o.endLong }));
    }

    // Once a second, only for a countdown with a Sec tile on screen: the seconds are worked out from the
    // same end the minutes are; when a minute turns over the whole readout is recomputed from the data layer.
    function sec() {
      if (reduced()) { render(); return; }
      if (!counting) { return; }
      var left = endsAt - now();
      if (!(left > 0) || Math.floor(left / TICK_MS) !== lastMin) { render(); return; }
      tiles.setSec(Math.floor((left % TICK_MS) / SEC_MS), !quiet);
    }

    render();
    tickers.push({ fn: render, root: root });
    if (withSec) {
      entry = { fn: sec, root: root, vis: true, on: counting };
      secs.push(entry);
      watchStage(entry);
    }
    ensureTicking();
  }

  /* ---- the header bar ---------------------------------------------------
     F mounts this itself on [data-ug-obar] (contract 8.8's own mountBar()
     row). Up to 3 messages in one grid cell (upgrade-offer-ui.css), ‹ ›
     switches which is visible, nothing rotates on its own. */
  function mountBar(el) {
    if (!el || el.__ugBar) { return; }
    el.__ugBar = true;

    var track = document.createElement('div');
    track.className = 'ug-obar__track';

    // LF2 fix (29 Sep 2026, fix_now): prev/count/next now live inside one
    // `.ug-obar__nav` wrapper (the prototype's `.obar__nav`, index.html
    // :1097), not as three loose siblings of the bar. That is what lets
    // the compaction CSS treat "‹ 1/3 ›" as a single unit -- pushed to the
    // end of the row on wide bars, dropped to its own right-aligned row
    // below 600px (upgrade-offer-ui.css) -- without reordering individual
    // buttons.
    var prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'ug-obar__prev';
    prevBtn.setAttribute('aria-label', T('offer.prev_message'));
    prevBtn.textContent = '‹';

    var counter = document.createElement('span');
    counter.className = 'ug-obar__count';

    var nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'ug-obar__next';
    nextBtn.setAttribute('aria-label', T('offer.next_message'));
    nextBtn.textContent = '›';

    var nav = document.createElement('span');
    nav.className = 'ug-obar__nav';
    nav.appendChild(prevBtn);
    nav.appendChild(counter);
    nav.appendChild(nextBtn);

    el.appendChild(track);
    el.appendChild(nav);

    var cells = [];
    var active = 0;

    function publishHeight() {
      try {
        var r = el.getBoundingClientRect();
        document.documentElement.style.setProperty('--ug-obar-h', (r && r.height ? r.height : 0) + 'px');
      } catch (e) { /* no layout engine in this environment: nothing to publish */ }
    }

    function buildCell(m) {
      var cell = document.createElement('span');
      cell.className = 'ug-obar__msg';

      var line = document.createElement('span');
      line.className = 'ug-obar__line';
      line.textContent = pick(m.text);
      cell.appendChild(line);

      // The Arabic line the English bar carries for Arabic readers; on a
      // translated page the main line already is the page language's text.
      if (!I18N) {
        var ar = document.createElement('span');
        ar.className = 'ug-obar__ar';
        ar.setAttribute('dir', 'rtl');
        ar.textContent = m.text.ar;
        cell.appendChild(ar);
      }

      if (m.endShort) {
        var until = document.createElement('span');
        until.className = 'ug-obar__until';
        until.textContent = T('offer.bar_until', { date: m.endShort });
        cell.appendChild(until);
      }

      if (m.kind === 'price') {
        var cdMount = document.createElement('span');
        cdMount.className = 'ug-obar__cd';
        mountCountdown(cdMount, { variant: 'inline' });
        cell.appendChild(cdMount);
      }

      if (m.link) {
        var terms = document.createElement('a');
        terms.className = 'ug-obar__terms';
        terms.href = m.link;
        terms.textContent = T('offer.terms');
        cell.appendChild(terms);
      }

      return cell;
    }

    function paint() {
      var list = messages();
      // CLS fix, 29 Sep 2026 (F14; Stage 2 content pass, upgrade-offer-ui.css's
      // own note above its [hidden] rule): [hidden] alone now RESERVES the
      // bar's space from first paint (a CSS concern, not this function's).
      // A confirmed-empty result (nothing to show at all) is a different
      // state from "not mounted yet" and must collapse for real, so it
      // gets its own marker class the CSS keys off with higher specificity.
      if (!list.length) { el.hidden = true; el.classList.add('ug-obar--empty'); return; }
      el.classList.remove('ug-obar--empty');
      el.hidden = false;

      if (cells.length !== list.length) {
        cells.forEach(function (c) { track.removeChild(c); });
        cells = list.map(function (m) {
          var cell = buildCell(m);
          track.appendChild(cell);
          return cell;
        });
        if (active >= cells.length) { active = 0; }
      }

      cells.forEach(function (cell, i) { cell.classList.toggle('is-active', i === active); });

      var multi = cells.length > 1;
      prevBtn.hidden = !multi;
      nextBtn.hidden = !multi;
      counter.hidden = !multi;
      counter.textContent = isolateText((active + 1) + ' / ' + cells.length);

      publishHeight();
    }

    prevBtn.addEventListener('click', function () {
      if (!cells.length) { return; }
      active = (active - 1 + cells.length) % cells.length;
      paint();
    });
    nextBtn.addEventListener('click', function () {
      if (!cells.length) { return; }
      active = (active + 1) % cells.length;
      paint();
    });

    // LF2 fix (29 Sep 2026, fix_now): --ug-obar-h has to stay correct
    // across a viewport-width change too, not just on mount/message-switch
    // -- an orientation flip or a font/breakpoint reflow can change the
    // bar's wrapped height without touching `messages()` at all, and a
    // stale value would leave assets/upgrade-shell.css's
    // `calc(-1 * var(--ug-obar-h))` wrapper offset wrong until the next
    // paint(). One `requestAnimationFrame`-coalesced listener, torn down
    // never (the bar mounts once per page, same as every other tracker in
    // this file).
    var resizeQueued = false;
    function onResize() {
      if (resizeQueued) { return; }
      resizeQueued = true;
      var raf = (typeof window.requestAnimationFrame === 'function')
        ? window.requestAnimationFrame
        : function (fn) { setTimeout(fn, 16); };
      raf(function () { resizeQueued = false; publishHeight(); });
    }
    try { window.addEventListener('resize', onResize); } catch (e) { /* no window.addEventListener in this environment */ }

    ready(function () {
      paint();
      tickers.push({ fn: paint, root: el });
      ensureTicking();
    });
  }

  /* ---- the footer's "Offer terms" reveal (F7's static mount) ----------- */
  /* The staff ribbon (snippets/upgrade-bf-preview-ribbon.liquid, printed hidden on an unpublished
     theme with the preview switch on): shown only in a genuine preview, with the launch checks this
     file can see from the browser (reduced prices, the offer window) unhidden when still unmet. */
  function revealPreviewRibbon() {
    if (!previewing()) { return; }
    var rb = null;
    try { rb = document.getElementById('UgBfPreviewRibbon'); } catch (e) { rb = null; }
    if (!rb) { return; }
    rb.hidden = false;
    try {
      var live = liveOffer();
      var pr = rb.querySelector('[data-ug-bf-check="prices"]');
      if (pr && !live && !anyOnOfferOrClosed()) { pr.hidden = false; }
      var api2 = offerApi();
      var win = rb.querySelector('[data-ug-bf-check="window"]');
      if (win && !live && !(api2 && typeof api2.windowOpen === 'function' && api2.windowOpen(now()))) { win.hidden = false; }
    } catch (e2) { /* a preview aid only */ }
  }

  function revealOfferTerms() {
    var li = null;
    try { li = document.querySelector('[data-ug-offer-terms]'); } catch (e) { li = null; }
    if (!li) { return; }
    ready(function () {
      var o = offer();
      if (!o || !o.termsUrl) { return; }
      li.hidden = false;
      var a = null;
      try { a = li.querySelector ? li.querySelector('a') : null; } catch (e) { a = null; }
      if (a) {
        a.href = o.termsUrl;
        /* Edit everything (7 Oct 2026): the footer prints its own editable
           label (Theme editor > Footer > "Offer terms" label, translated in
           Translate & Adapt). It used to be overwritten here, so editing it
           did nothing while an offer ran. Kept now; an empty link only gets
           this file's word. */
        var shown = '';
        try { shown = String(a.textContent == null ? '' : a.textContent); } catch (e) { shown = ''; }
        if (!/\S/.test(shown)) { a.textContent = T('offer.terms'); }
      }
    });
  }

  /* ---- the reload rule ---------------------------------------------------
     A page left open crosses the offer's start or end: the theme reloads
     itself once at that instant (never twice for the same end date, via
     sessionStorage), because Shopify's own scheduled price change needs a
     reload anyway for the new price to show. */
  // setTimeout's delay is a 32-bit signed int internally (~24.8 days,
  // 2147483647ms) -- MDN documents that a longer delay overflows and the
  // browser can fire it immediately. An offer configured more than 24.8
  // days before it starts (e.g. Black Friday set up in October) would
  // therefore reload the page at once on the first view of every session,
  // rather than waiting for the real boundary. FIXED, 29 Sep 2026 (review
  // fix, should-fix): cap the timer and re-arm rather than schedule the
  // full wait in one setTimeout call.
  var MAX_TIMEOUT_MS = 2147483647;
  var reloadTimer = null;
  function scheduleWindowReload() {
    var raw = rawOffer();
    var api2 = offerApi();
    if (!raw || !api2 || typeof api2._cairoWallToUtcMs !== 'function') { return; }
    var startMs = api2._cairoWallToUtcMs(raw.starts);
    var endMs = api2._cairoWallToUtcMs(raw.ends);
    var candidates = [startMs, endMs].filter(function (t) { return typeof t === 'number' && isFinite(t); });
    if (!candidates.length) { return; }
    var n = now();
    var next = null;
    candidates.forEach(function (t) {
      if (t > n && (next === null || t < next)) { next = t; }
    });
    if (next === null) { return; }
    // Cleared and re-armed on every call (scheduleWindowReload also runs
    // from watchForReturn's visibilitychange/pageshow listeners) so repeat
    // calls cannot stack more than one pending timer.
    if (reloadTimer !== null) { clearTimeout(reloadTimer); reloadTimer = null; }
    // FIXED, 29 Sep 2026 (review fix, should-fix): keyed on the boundary
    // actually being waited for (`next`), not always `raw.ends` -- a shared
    // key meant the start-boundary reload (or a spurious one) could
    // permanently suppress the later end-boundary reload for the rest of
    // the session, and the reverse.
    var key = 'ug.offer.reloaded.' + next;
    var wait = Math.max(0, next - n);
    if (wait > MAX_TIMEOUT_MS) {
      reloadTimer = setTimeout(scheduleWindowReload, MAX_TIMEOUT_MS);
      return;
    }
    reloadTimer = setTimeout(function () {
      reloadTimer = null;
      var already = false;
      try { already = sessionStorage.getItem(key) === '1'; } catch (e) { already = false; }
      if (already) { return; }
      try { sessionStorage.setItem(key, '1'); } catch (e) { /* best effort: worst case it reloads more than once */ }
      try {
        if (location && typeof location.reload === 'function') { location.reload(); }
      } catch (e) { /* nothing more this file can do */ }
    }, wait);
  }

  function watchForReturn() {
    try {
      document.addEventListener('visibilitychange', function () {
        if (!awake()) { pauseTickers(); return; }
        resumeTickers();
        scheduleWindowReload();
      });
    } catch (e) { /* no document.addEventListener in this environment */ }
    try {
      if (window.addEventListener) {
        window.addEventListener('pageshow', function () { resumeTickers(); scheduleWindowReload(); });
      }
    } catch (e) { /* no window.addEventListener in this environment */ }
  }

  var api = {
    ready: ready,
    now: now,
    clockTrusted: clockTrusted,
    offer: offer,
    messages: messages,
    forSku: forSku,
    cardOffer: cardOffer,
    previewing: previewing,
    paintCards: paintCards,
    mountCountdown: mountCountdown,
    mountBar: mountBar,
    format: format
  };

  window.UpgradeOfferUI = api;

  syncClock();
  ready(function () {
    var bar = null;
    try { bar = document.querySelector('[data-ug-obar]'); } catch (e) { bar = null; }
    if (bar) { mountBar(bar); }
    paintCards(document);
    revealPreviewRibbon();
    revealOfferTerms();
    scheduleWindowReload();
  });
  watchForReturn();
})();
